import { ARENA_QUALIFICATION_FREE_TEXT_OPTION_ID, type ArenaQualificationQuestionDto } from "./arena-qualification.dto";

/**
 * The code-owned question set for DEV-703 — the ONE definition.
 *
 * It lives in `@cede/types` because three places need exactly the same set and
 * had already drifted apart when they each kept their own: the API seeds it and
 * validates CMS edits against it, the migration inserts it, and Arena renders
 * it. The first integration attempt shipped a 4-point profitability scale on the
 * backend against a 5-point one on the frontend, which would have 422'd every
 * "Up a bit" answer.
 *
 * The CMS may restyle this (text, hint, option labels, order) but not
 * change any id or kind — those are the analytics keys every future segment is
 * built on.
 *
 * ORDERING RULE: every scale runs worst/least LEFT to best/most RIGHT.
 */
export const CANONICAL_ARENA_QUALIFICATION_QUESTIONS: ArenaQualificationQuestionDto[] = [
  {
    id: "persona",
    kind: "multi_choice",
    text: "Which of these do you relate to the most?",
    hint: "Pick as many as fit.",
    // Ordered by how likely each answer is, so the common ones are found first.
    options: [
      { id: "perps_trader", label: "Perps trader" },
      { id: "prop_firm_user", label: "Prop firm user" },
      { id: "casual_trader", label: "Casual trader" },
      { id: "advanced_trader", label: "Advanced trader" },
      { id: "airdrop_farmer", label: "Airdrop farmer" },
      { id: "prediction_market_user", label: "Prediction market user" },
      { id: "casino_player", label: "Casino player" },
      { id: "fantasy_sports_player", label: "Fantasy sports player" },
      { id: ARENA_QUALIFICATION_FREE_TEXT_OPTION_ID, label: "Something else" },
    ],
  },
  {
    id: "profitability",
    kind: "single_choice",
    text: "How profitable are you, counting everything you've put in?",
    // Five points, symmetric about break-even. "Up a bit" is NOT in the original
    // Notion list: without it a modestly profitable trader had to claim "up a
    // lot" or undersell as break-even, and the scale was lopsided in both
    // directions. `dont_know` sits off the scale — it is not a point on it.
    options: [
      { id: "down_by_a_lot", label: "Down a lot" },
      { id: "down_but_not_far", label: "Down a bit" },
      { id: "roughly_break_even", label: "Break-even" },
      { id: "profitable_but_not_far", label: "Up a bit" },
      { id: "profitable_by_a_lot", label: "Up a lot" },
      { id: "dont_know", label: "Not sure" },
    ],
  },
  {
    id: "crypto_holding",
    kind: "single_choice",
    text: "Do you hold crypto?",
    // Least → most, by how readily the money could reach a buy-in.
    options: [
      { id: "not_yet", label: "Not yet" },
      { id: "yes_self_custody", label: "Yes, in a self-custody wallet" },
      { id: "yes_on_exchange", label: "Yes, on an exchange" },
    ],
  },
  {
    id: "prop_firm_payout",
    kind: "single_choice",
    text: "Have you already received a payout from a prop firm funded account?",
    showIf: { questionId: "persona", optionIds: ["prop_firm_user"] },
    options: [
      { id: "no_not_yet", label: "No, not yet" },
      { id: "yes_once", label: "Yes, once" },
      { id: "yes_more_than_once", label: "Yes, more than once" },
    ],
  },
  {
    id: "prop_firm_spend_tracking",
    kind: "single_choice",
    text: "Do you track all your prop firm challenge spending?",
    showIf: { questionId: "persona", optionIds: ["prop_firm_user"] },
    options: [
      { id: "no", label: "No" },
      { id: "roughly", label: "Roughly" },
      { id: "yes_precisely", label: "Yes, precisely" },
    ],
  },
  {
    id: "how_did_you_hear",
    kind: "single_choice",
    text: "How did you hear about us?",
    // Self-reported attribution: the only way to see word of mouth and DM
    // referrals, which carry no UTM. Optional because it serves us rather than
    // the segmentation the rest of the form exists for — gating on it would cost
    // answers that matter more.
    optional: true,
    options: [
      { id: "x", label: "X / Twitter" },
      { id: "discord", label: "Discord" },
      { id: "telegram", label: "Telegram" },
      { id: "youtube", label: "YouTube" },
      { id: "tiktok", label: "TikTok" },
      { id: "instagram", label: "Instagram" },
      { id: "friend", label: "A friend or someone I know" },
      { id: "creator", label: "A creator or streamer" },
      { id: "search", label: "A search engine" },
      { id: "ad", label: "An ad" },
      { id: "cede", label: "I already use Cede Hub or Flow" },
      { id: ARENA_QUALIFICATION_FREE_TEXT_OPTION_ID, label: "Something else" },
    ],
  },
];
